import * as React from "react";

import { cn } from "../../utils/cn";

import { useResponsiveAddress } from "./useResponsiveAddress";

export interface ResponsiveAddressProps {
    /** The full Solana address. */
    address: string;
    /**
     * `false` (default): a block that fills its cell or column, and its own
     * width is the budget. `true`: an inline address inside a sentence; the
     * parent's width is the budget.
     */
    inline?: boolean;
    /**
     * Text glued to the address on both sides, e.g. the parentheses around it
     * in a sentence, so they never wrap away from it. Counted in the fit.
     */
    prefix?: string;
    suffix?: string;
    /** Text style, from the caller: a `Text` variant, `font-numeric`, colour. */
    className?: string;
}

/**
 * A Solana address shown in full, shortening in the middle only when its box
 * is too narrow: full → 8…8 → 4…4. See `ResponsiveAddress.intent.md`.
 *
 * The full address stays in the DOM: a visually-hidden copy for screen
 * readers, the `title` for hover, and a copy of the visible text puts the
 * full address on the clipboard.
 */
export function ResponsiveAddress({
    address,
    inline = false,
    prefix = "",
    suffix = "",
    className
}: ResponsiveAddressProps): React.ReactElement {
    const ref = React.useRef<HTMLSpanElement>(null);
    const { text, tier } = useResponsiveAddress({
        address,
        ref,
        measure: inline ? "parent" : "self",
        prefix,
        suffix
    });

    const onCopy = (event: React.ClipboardEvent<HTMLSpanElement>): void => {
        const selection = window.getSelection();
        const element = ref.current;
        if (!selection || selection.rangeCount === 0 || !element) return;
        // Only a selection wholly inside the address; a wider one (a whole
        // table row) copies as the page shows it.
        if (!element.contains(selection.getRangeAt(0).commonAncestorContainer))
            return;
        event.clipboardData.setData("text/plain", address);
        event.preventDefault();
    };

    return (
        <span
            ref={ref}
            data-slot="responsive-address"
            data-tier={tier}
            title={address}
            onCopy={onCopy}
            className={cn(
                inline
                    ? "inline-block max-w-full align-bottom"
                    : "block w-full min-w-0",
                "overflow-hidden whitespace-nowrap",
                className
            )}
        >
            <span aria-hidden="true">{`${prefix}${text}${suffix}`}</span>
            <span className="sr-only">{`${prefix}${address}${suffix}`}</span>
        </span>
    );
}
